import React from 'react';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import Button from './Button';
import ProgressBar from './ProgressBar';
import SectionLabel from './SectionLabel';
import PageHeader from './PageHeader';
import Stepper from './Stepper';
import { cn } from './cn';

/** Schema-independent, responsive questionnaire workspace. */
function FormFlow({ title, stepLabel, progress, savedLabel, children, onBack, onNext,
  nextLabel = 'Continue', backDisabled, nextDisabled, backLoading, nextLoading, hint }) {
  return <div className="bun w-full">
    <div className="mr-auto flex min-h-[calc(100dvh-10rem)] w-full max-w-[720px] flex-col font-instrument sm:px-9 sm:pb-12 sm:pt-10">
      <header className="sticky top-0 z-10 bg-bun-canvas px-[18px] py-3 sm:static sm:p-0">
        <div className="flex items-center gap-3">
          <SectionLabel className="hidden shrink-0 sm:block">{title} · {stepLabel}</SectionLabel>
          <span className="min-w-0 flex-1 text-bun-body font-semibold text-bun-text sm:hidden">{title}</span>
          <ProgressBar value={progress} tone="ink" size="sm" aria-label={`${title} progress`} className="hidden flex-1 sm:block" />
          <span className="text-bun-meta text-bun-stone sm:hidden">{stepLabel}</span>
          <span role="status" className="hidden text-bun-meta text-bun-stone sm:block">{savedLabel}</span>
        </div>
        <ProgressBar value={progress} tone="ink" size="sm" aria-label={`${title} progress`} className="mt-3 sm:hidden" />
      </header>
      <div className="flex-1 px-[18px] pb-10 pt-6 sm:px-0 sm:pt-9">{children}</div>
      <footer className="sticky bottom-0 z-10 flex items-center gap-2 border-t border-bun-line-soft bg-bun-canvas px-[18px] pt-[18px] pb-[max(18px,env(safe-area-inset-bottom))] sm:static sm:px-0 sm:pb-0">
        {onBack && <Button variant="secondary" onClick={onBack} disabled={backDisabled} loading={backLoading} aria-label="Back">
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" /><span className="hidden sm:inline">Back</span>
        </Button>}
        <span className="hidden flex-1 sm:block" />
        {hint && <span className="hidden text-bun-meta text-bun-stone sm:block">{hint}</span>}
        <Button onClick={onNext} disabled={nextDisabled} loading={nextLoading} className="flex-1 sm:flex-none">
          {nextLabel}<ChevronRightIcon className="hidden h-4 w-4 sm:block" aria-hidden="true" />
        </Button>
      </footer>
    </div>
  </div>;
}

function Complete({ title, description, actions, className }) {
  return <div className={cn('bun flex min-h-[calc(100dvh-10rem)] flex-col items-center justify-center px-[18px] py-12 text-center', className)}>
    <span className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-bun-success-bg text-bun-success">
      <CheckIcon className="h-7 w-7" aria-hidden="true" />
    </span>
    <h1 className="max-w-lg font-instrument text-bun-display font-semibold leading-tight tracking-tight text-bun-text">{title}</h1>
    <p className="mt-4 max-w-lg text-bun-body text-bun-slate leading-relaxed">{description}</p>
    <div className="mt-6 flex flex-wrap justify-center gap-2">{actions}</div>
  </div>;
}

/** Multi-selection workspace: shared desktop toolbar and mobile progress/footer. */
function Selection({ title, subtitle, steps, current, instruction, count, target, filters, mobileFilters, alerts, action, children }) {
  const counter = <div role="status" aria-label="Selected count" className="flex shrink-0 items-baseline gap-[6px] sm:rounded-bun sm:border sm:border-bun-line-soft sm:px-[14px] sm:py-2">
    <span className="order-2 text-bun-meta text-bun-stone sm:order-none">Selected</span>
    <span className={cn('text-bun-number font-semibold tabular-nums sm:text-bun-metric', target && count > target ? 'text-bun-warning' : 'text-bun-text')}>{count}</span>
    {target && <span className="text-bun-body text-bun-stone">/ {target}</span>}
  </div>;
  return <div className="bun w-full">
    <div className="mr-auto max-w-7xl px-[18px] pb-[calc(120px+env(safe-area-inset-bottom))] pt-3 font-instrument sm:px-9 sm:pb-14 sm:pt-[26px]">
      <header className="sticky top-0 z-20 bg-bun-canvas pb-[14px] sm:border-b sm:border-bun-line-soft">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:gap-6">
          <div className="hidden min-w-0 flex-1 sm:block"><PageHeader title={title} subtitle={subtitle} /></div>
          <div className="flex items-center justify-between gap-3 sm:hidden">
            <h1 className="m-0 font-instrument text-bun-heading font-semibold leading-tight tracking-tight text-bun-text">{title}</h1>
            <span className="shrink-0 text-bun-meta text-bun-stone">{steps[current]?.label} · {current + 1}/{steps.length}</span>
          </div>
          <Stepper steps={steps} current={current} variant="inline" mobileBars ariaLabel={`${title} progress`} className="sm:pt-1" />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-[14px]">
          <p aria-live="polite" aria-atomic="true" className="m-0 w-full text-bun-heading font-strong leading-relaxed text-bun-text sm:min-w-[240px] sm:flex-1">{instruction}</p>
          <div className="hidden items-center gap-[14px] sm:flex sm:flex-wrap">{counter}{filters}{action}</div>
        </div>
        {alerts && <div className="mt-[10px] space-y-2">{alerts}</div>}
        {mobileFilters && <div className="mt-3 flex flex-wrap gap-[6px] sm:hidden">{mobileFilters}</div>}
      </header>
      <div className="mt-[14px] grid grid-cols-1 gap-2 sm:mt-5 sm:grid-cols-2 sm:gap-3 lg:grid-cols-4">{children}</div>
      <footer aria-label="Selection actions" className="fixed inset-x-0 bottom-0 z-50 flex items-center gap-3 border-t border-bun-line-soft bg-bun-canvas px-[18px] pt-3 pb-[max(18px,env(safe-area-inset-bottom))] sm:hidden">
        {counter}<div className="flex min-w-0 flex-1 [&>*]:w-full">{action}</div>
      </footer>
    </div>
  </div>;
}

FormFlow.Selection = Selection;
FormFlow.Complete = Complete;
export default FormFlow;
